"use client";

import * as React from "react";
import { showToast } from "@/components/ui/sonner";
import {
  claimToastGeneration,
  currentToastGeneration,
  releaseToastGeneration,
} from "@/lib/toast-dismiss-generation";
import { pluralize } from "@/lib/string/pluralize";
import { useBulkMoveJobPoll } from "@/features/jobs/hooks/use-bulk-move-job-poll";

export type BulkMoveProgressSession = {
  jobId: string;
  bulkMoveJobId: string;
  /** Local move count from BulkActionResponse.updated — title "Moving N". */
  movedCount: number;
  toastId: string;
};

export function BulkMoveProgressToast({
  session,
  onFinished,
}: {
  session: BulkMoveProgressSession;
  onFinished: (bulkMoveJobId: string) => void;
}) {
  const { data, isTerminal, stuckStarting, timedOut } = useBulkMoveJobPoll({
    jobId: session.jobId,
    bulkMoveJobId: session.bulkMoveJobId,
    enabled: true,
  });

  const userDismissedRef = React.useRef(false);
  const closingRef = React.useRef(false); // terminal complete only
  const claimRef = React.useRef(0);

  const movedLabel = `${session.movedCount} ${pluralize(session.movedCount, "candidate")}`;
  const title = `Moving ${movedLabel}…`;

  const description = React.useMemo(() => {
    if (stuckStarting) {
      return "Still starting… ATS sync hasn't begun yet.";
    }
    const eligible = data?.push_eligible ?? 0;
    if (eligible > 0) {
      const done = (data?.push_ok ?? 0) + (data?.push_failed ?? 0);
      return `Pushing updates to ATS · ${done} / ${eligible}`;
    }
    return "Saving locally — preparing ATS updates…";
  }, [stuckStarting, data?.push_eligible, data?.push_ok, data?.push_failed]);

  React.useEffect(() => {
    if (userDismissedRef.current || closingRef.current) return;
    const g = claimToastGeneration(session.toastId);
    claimRef.current = g;
    showToast.loading(title, {
      id: session.toastId,
      description,
      duration: Infinity,
      onDismiss: () => {
        // Dismiss ≠ cancel — server work continues.
        if (closingRef.current) return;
        // Ignore stale onDismiss from StrictMode cleanup / sonner exit animation.
        if (currentToastGeneration(session.toastId) !== g) return;
        userDismissedRef.current = true;
        releaseToastGeneration(session.toastId, g);
        onFinished(session.bulkMoveJobId);
      },
    });
  }, [title, description, session.toastId, session.bulkMoveJobId, onFinished]);

  React.useEffect(() => {
    if (userDismissedRef.current || closingRef.current) return;

    if (timedOut) {
      closingRef.current = true;
      releaseToastGeneration(session.toastId, claimRef.current);
      onFinished(session.bulkMoveJobId);
      showToast.dismiss(session.toastId);
      showToast.warning("ATS sync is still running", {
        description:
          "This is taking longer than expected. Stages are already updated — refresh the pipeline to check progress.",
        duration: Infinity,
      });
      return;
    }

    if (!isTerminal || !data) return;

    closingRef.current = true;
    releaseToastGeneration(session.toastId, claimRef.current);
    onFinished(session.bulkMoveJobId);
    showToast.dismiss(session.toastId);

    if (data.status === "completed") {
      if (data.push_eligible === 0) {
        showToast.success(`Moved ${movedLabel}`, {
          description: "No ATS push needed.",
        });
      } else {
        showToast.success(`Moved ${movedLabel}`, {
          description: "All ATS updates finished.",
        });
      }
      return;
    }

    if (data.status === "completed_with_errors") {
      showToast.warning(
        `${data.push_failed} ATS ${pluralize(data.push_failed, "update")} failed — stages already changed locally`,
      );
      return;
    }

    if (data.status === "failed") {
      showToast.error("Move saved locally; ATS fan-out failed", {
        description: data.error_message ?? undefined,
      });
    }
  }, [
    isTerminal,
    timedOut,
    data,
    movedLabel,
    session.toastId,
    session.bulkMoveJobId,
    onFinished,
  ]);

  React.useEffect(() => {
    return () => {
      if (userDismissedRef.current || closingRef.current) return;
      // Bump so any in-flight onDismiss from this claim is stale. Defer dismiss
      // so StrictMode remount can reclaim the same toast id first; otherwise
      // sonner's delayed remove kills the remounted loading toast and can clear
      // the session via onDismiss.
      const expected = claimToastGeneration(session.toastId);
      queueMicrotask(() => {
        if (currentToastGeneration(session.toastId) !== expected) return;
        showToast.dismiss(session.toastId);
        releaseToastGeneration(session.toastId, expected);
      });
    };
  }, [session.toastId]);

  return null;
}
